/* ============================================
BOOST CUSTOMISATION START
Cart-level Fee Manager + Decoration Qty Sync + Orphan Cleanup
assets/boost-cart-fee-manager.js
(session 5, v2.5.0 — KEY-ADDRESSED WRITES + DECORATION-PRESENCE TEST)

==============================================================
v2.5.0 changes (2026-09-10, orphaned Artwork Setup Fee)
==============================================================

THE DEFECT REPORTED
  A cart holding one UNDECORATED garment kept the $70 Artwork Setup
  Fee. It survived every refresh. Live cart dump (2026-09-10):

    { "_fee_type": "artwork_setup" }                      qty 1
    { "_product_key": "pk-0ff902" }   Westgarth GW-1637L, qty 1

  The garment carries a _product_key but has NO decoration siblings.

TWO BUGS, INTERLOCKING. Both are fixed here.

  BUG 1 (why the fee stayed, and stayed permanently).
    Pass 2 classified a group as decorated on two tests only: the
    garment line is present, and the group is not sample-only. It
    never checked that the group actually CONTAINS a decoration. So a
    keyed group with zero decoration carriers marked the $70 as
    required forever. computeFeePlan then read needed=true,
    present=true, qty=1, returned needs=null, planAndExecute returned
    null, and the reconcile recorded a no-op. The manager was running
    correctly and deciding the cart was already right.

    Fix: a group counts as decorated only when it holds at least one
    _product_option:'decoration' line (g.decorationIndices.length).
    This also makes any already-corrupted cart self-heal on the next
    reconcile, with no data repair needed.

  BUG 2 (how the garment lost its decorations in the first place).
    planAndExecute built a POSITIONAL /cart/update.js array from a
    /cart.js snapshot. When the customer removes several lines quickly
    the cart shrinks between the read and the POST, every entry after
    the removed line shifts by one, and a zero intended for an orphan
    carrier lands on the NEXT line instead, which can be a live
    decoration carrier belonging to a garment that is staying. That
    produces exactly the artefact above: garment kept, its _product_key
    kept, its decorations silently zeroed. A length mismatch is also
    possible, in which case the old code swallowed the 422 in a catch
    that only logged, with no retry.

    Fix: address every line by its immutable Shopify line `key`, never
    by position, via an `updates` OBJECT. This is the identical fix
    already locked into boost-bulk-grid.js on 2026-05-23 for the same
    bug class (see its KEY-ADDRESSED REWRITE header, which records the
    old positional array "zeroed unrelated lines (other carriers, the
    $50 fee)"). The fee manager was never migrated to it. It is now.
    A failed write is also retried once, forced, instead of being
    logged and abandoned.

NOT CHANGED, deliberately
  - The picker stays the sole fee ADDER, this file the sole REMOVER
    and ENFORCER. Pass 4 still ignores an 'add' plan.
  - Triggers, debounce, no-op cooldown and observers are untouched.
  - addFee() remains present and unreferenced, as before.

==============================================================
v2.2.5 changes (from T-0518 test results 2026-05-18)
==============================================================

PROBLEM DIAGNOSED IN v2.2.4 TESTING
  T-0518-034/035/036 all failed with the same pattern: when a customer
  changes a decorated garment's quantity from the cart drawer or cart
  page, the decoration line stays at qty 1 (server-side), so the cart
  total under-charges. Hard-refresh fixes it, which confirms the
  decoration-qty sync logic itself works — but only on page load, not
  during interactive flows.

  Diagnostic evidence (Phil's dev-tools, 2026-05-18):
    typeof window.fetch.toString().slice(0, 200) returned MINIFIED
    third-party wrapper code, NOT v2.2.4's boost-cart-sync.js patch.

  Root cause: a third-party script (Shopify analytics, Klaviyo, or
  another installed app) wraps window.fetch AFTER our defer-loaded
  scripts run, burying our patched fetch under their wrapper. Our
  reconcile callback never fires for interactive /cart/change.js
  calls. The wrapper still calls through to native fetch so cart
  mutations succeed, but our scheduleReconcile() trigger is lost.

THE v2.2.5 SOLUTION
  Replace fetch-patching with MutationObserver. The theme repaints
  the cart-drawer (#cart) and cart-page (.form-cart) DOM after every
  qty change. That DOM mutation is impossible for third-party scripts
  to suppress — it's the signal we listen to instead of fetch calls.

  Also: unify boost-cart-sync.js's decoration-qty propagation into the
  fee-manager's reconcile. Previously two independent IIFEs both
  patched fetch and both scheduled reconciles, racing each other and
  potentially double-refreshing the drawer. The unified reconciler:
    1. Fetches /cart.js to read true server state
    2. Computes EVERYTHING in one pass:
        - Decoration-line qty corrections (was cart-sync's job)
        - Fee add/remove/force-qty-1 (fee-manager's existing job)
        - Orphan-carrier zeroing (fee-manager's existing job)
    3. Issues ONE /cart/update.js (or /cart/add.js for fee-add) with
       all corrections combined
    4. Calls refreshCartUi(response) to repaint once

  boost-cart-sync.js is now an empty stub (kept in /assets to avoid
  404s if the script tag remains in theme.liquid during deployment).

  Debounce reduced from 120ms → 75ms because there's only one
  reconciler now — no race-coordination delay needed.

TRIGGERS (v2.2.5)
  1. MutationObserver on #cart (drawer container).
     Fires when the theme repaints the drawer after qty change, trash,
     add-to-cart, or our own refreshCartUi call. We guard against our
     own refresh causing a re-entry loop via the reconcileInFlight
     flag — same pattern v2.2.4 used.

  2. MutationObserver on the cart page's section wrapper
     (#shopify-section-<cartPageTemplate>). Same purpose for the
     cart page.

  3. DOMContentLoaded / immediate init reconcile. Covers cases where
     a previous session left the cart in a mismatched state (no
     mutation event fires for arriving on the page with a stale cart).

  4. document 'boost:cart-updated' event. Custom event the picker
     dispatches after its own /cart/add.js calls; lets the fee manager
     react instantly to picker adds without waiting for DOM-observer
     debounce.

WHAT WE NO LONGER DO
  - window.fetch patching. Removed entirely. We don't fight the
    third-party wrapper chain.
  - XHR patching. Same reason.

==============================================================
v2.2.4 history (still relevant)
==============================================================
  - TRACKER REMOVAL: the picker no longer writes a separate "artwork
    tracker" line item for decorated or sample-only groups. The
    _sample_only flag now lives on the GARMENT line directly. The
    fee manager's classification reads _sample_only from EITHER the
    garment line OR (for legacy carts created before v2.2.4) the
    tracker line — backward compat is preserved.
  - The B5 fix (decoration qty propagation) was originally in a
    separate boost-cart-sync.js. v2.2.5 unifies it back into this
    file.

==============================================================
v2.2.3 / v2.2.2 history (still relevant)
==============================================================
  - Mutation requests include `sections: 'side-cart,<cartPageId>'`.
  - refreshCartUi calls window.ajaxCart.load(response) for drawer +
    header; injects cart-page section markup manually.
  - After cart-page section injection, dispatches window 'ajaxCart'
    event so the theme's listener re-binds event handlers
    (custom-async.js line 7231).

==============================================================
v2.2 ARCHITECTURE (still the baseline)
==============================================================
  - Single source of truth: ONLY watches `artwork-setup-fee` variant ID
    (passed via window.BoostFeeConfig.feeVariantId).
  - Single-flight reconcile with trailing-edge dedup.

==============================================================
OPERATING PRINCIPLE (v2.2.5)
==============================================================
  When the cart drawer or cart page DOM repaints (or DOMContentLoaded
  fires, or the picker emits boost:cart-updated):
    1. Debounce → reconcile.
    2. Fetch /cart.js. Identify _product_key groups. Classify each as
       "decorated", "sample-only" or "keyed but bare". Decorated
       requires BOTH that the group is not sample-only AND that it
       holds at least one _product_option:'decoration' line (v2.5.0).
    3. Compute three correction sets:
        a. Decoration-qty mismatches (decoration line qty !=
           garment qty for any decorated group).
        b. Orphan carriers (carrier present, garment missing) →
           zero them.
        c. Fee state:
           - 0 decorated AND fee present  → REMOVE fee.
           - ≥1 decorated AND fee absent  → ADD fee at qty 1.
           - ≥1 decorated AND fee qty > 1 → FORCE qty 1.
    4. Build ONE KEY-ADDRESSED /cart/update.js payload (an `updates`
       object keyed by each line's immutable `key`, v2.5.0) combining
       (a), (b), and (c) where (c) is "remove" or "force-1". If (c) is
       "add", fire /cart/add.js after that update settles.
    5. Pipe responses through refreshCartUi() to repaint drawer +
       cart page + header.
============================================ */
(function () {
  'use strict';

  /* ============================================
  v2.2.5 — Debounce reduced from 120ms to 75ms.
  With one unified reconciler (no race coordination needed), the
  delay only exists to coalesce multiple rapid DOM mutations into a
  single reconcile. 75ms is enough for the theme to settle its own
  repaint before we read /cart.js.
  ============================================ */
  var POST_MUTATION_DELAY = 75;
  var PRODUCT_KEY_PREFIX = 'pk-';

  function getFeeVariantId() {
    var cfg = window.BoostFeeConfig || {};
    var n = Number(cfg.feeVariantId);
    if (isNaN(n) || n <= 0) return null;
    return n;
  }

  /* ============================================
  BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2):
  $50 Embroidery Setup Fee variant. Resolved live by handle in
  layout/theme.liquid and exposed on window.BoostFeeConfig.embroideryFeeVariantId.
  Returns null when not configured (feature inert) so the manager keeps
  working with only the $70 fee if the embroidery carrier is missing.
  ============================================ */
  function getEmbroideryFeeVariantId() {
    var cfg = window.BoostFeeConfig || {};
    var n = Number(cfg.embroideryFeeVariantId);
    if (isNaN(n) || n <= 0) return null;
    return n;
  }

  /* ============================================
  BOOST CUSTOMISATION 2026-07-02 (WS2 - generalised fee engine):
  Full setup-fee handle -> variantId map, resolved live in layout/theme.liquid
  and exposed on window.BoostFeeConfig.feeVariants. This is the single source of
  truth for every managed fee. Returns {} when not present so the manager falls
  back to the two legacy IDs ($70 artwork, $50 embroidery).
  ============================================ */
  function getFeeVariantsMap() {
    var cfg = window.BoostFeeConfig || {};
    return (cfg.feeVariants && typeof cfg.feeVariants === 'object') ? cfg.feeVariants : {};
  }

  /* ============================================
  BOOST v2.2.1 SAMPLE-ONLY FIX — helper
  Returns true if a carrier or garment line represents a sample-only
  group. Primary signal: _sample_only === 'true'. Secondary signal:
  _decoration_type === 'Sample only' (display string; checked as a
  fallback in case the boolean isn't present on legacy carts).
  ============================================ */
  function isSampleOnlyMarker(props) {
    if (!props) return false;
    if (props._sample_only === 'true' || props._sample_only === true) return true;
    if (props._decoration_type === 'Sample only') return true;
    return false;
  }

  /* ============================================
  BOOST v2.2.2 UI REFRESH — helpers (unchanged in v2.2.5)
  ============================================ */

  // Discover the cart page section ID from the DOM, matching the theme's
  // own discovery logic in custom-async.js (line 6588). Returns null if
  // the cart page section isn't on the current page (e.g. on a PDP).
  function getCartPageSectionId() {
    var inner = document.querySelector('.form-cart, .cart-empty');
    if (!inner) return null;
    var wrapper = inner.parentElement;
    if (!wrapper || !wrapper.id) return null;
    if (wrapper.id.indexOf('shopify-section-') !== 0) return null;
    return wrapper.id.replace('shopify-section-', '');
  }

  // Build the comma-separated `sections` string to request from Shopify.
  function getSectionsList() {
    var sections = 'side-cart';
    var cartPageId = getCartPageSectionId();
    if (cartPageId) sections += ',' + cartPageId;
    return sections;
  }

  function refreshCartUi(response) {
    if (!response || !response.sections) return;
    var sideCartMarkup = response.sections['side-cart'];
    try {
      if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
        // Second arg `false` = do NOT auto-open the drawer.
        window.ajaxCart.load(response, false);
      }
    } catch (err) {
      console.warn('[boost-cart-fee-manager] ajaxCart.load failed:', err);
    }

    // Defensive: if the drawer's #cart container exists and the
    // markup didn't make it in, inject directly. From v2.2.4.
    try {
      if (sideCartMarkup) {
        var cartContainer = document.getElementById('cart');
        if (cartContainer) {
          var parsed = new DOMParser().parseFromString(sideCartMarkup, 'text/html');
          var newInner = parsed.querySelector('#shopify-section-side-cart');
          var newItemCount = newInner
            ? newInner.querySelectorAll('.l4ca > li').length
            : parsed.querySelectorAll('.l4ca > li').length;
          var renderedItemCount = cartContainer.querySelectorAll('.l4ca > li:not(.removing)').length;
          if (newInner && newItemCount !== renderedItemCount) {
            cartContainer.innerHTML = newInner.innerHTML;
            window.dispatchEvent(new Event('ajaxCart'));
          }
        }
      }
    } catch (err) {
      console.warn('[boost-cart-fee-manager] drawer fallback refresh failed:', err);
    }

    // Cart page section refresh + ajaxCart event for re-binding handlers.
    try {
      var cartPageId = getCartPageSectionId();
      if (cartPageId && response.sections[cartPageId]) {
        var wrapper = document.getElementById('shopify-section-' + cartPageId);
        if (wrapper) {
          var parsedPage = new DOMParser().parseFromString(
            response.sections[cartPageId],
            'text/html'
          );
          var newPageInner = parsedPage.querySelector('#shopify-section-' + cartPageId);
          if (newPageInner) {
            wrapper.innerHTML = newPageInner.innerHTML;
            window.dispatchEvent(new Event('ajaxCart'));
          }
        }
      }
    } catch (err) {
      console.warn('[boost-cart-fee-manager] cart page refresh failed:', err);
    }
  }

  /* ============================================
  v2.2.5 RECONCILE — unified
  Combines decoration-qty sync, orphan cleanup, and fee management.
  ============================================ */

  var reconcileScheduled = false;
  var reconcileInFlight = false;
  var trailingReconcileQueued = false;

  // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2) — NO-OP COOLDOWN.
  // The drawer MutationObserver watches the whole drawer subtree, so the
  // "You may also like" upsell and section repaints fire handleMutation
  // repeatedly. Each schedules a cart.js read that usually finds NOTHING to
  // correct (a no-op), but the reads pile up and are slow (>1s with large
  // decorated groups). After a reconcile that finds nothing to do, we
  // record the cart's "fingerprint" (item_count + a token) and a timestamp;
  // subsequent reconcile requests within NOOP_COOLDOWN_MS are skipped UNLESS
  // the cart actually changed (item_count differs) since the last no-op.
  // This collapses the read storm without narrowing the observer (which
  // risks missing a real line-item change).
  var lastNoopAt = 0;
  var NOOP_COOLDOWN_MS = 1200;

  // suppressMutationObserver: while WE are mutating the cart and
  // refreshing the UI, MutationObserver callbacks would otherwise
  // schedule another reconcile from our own DOM repaint. Set true
  // around our refresh; cleared on next animation frame.
  var suppressMutationObserver = false;

  // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): cheap DOM line count.
  // Counts cart rows via the theme's [data-line] attribute (present on both
  // the drawer and cart-page rows). Used by the no-op cooldown to detect a
  // REAL line change (e.g. a user removing an item) during the cooldown
  // window, so we don't suppress a reconcile that's actually needed.
  function domLineCount() {
    try {
      return document.querySelectorAll('#cart [data-line], .main-cart [data-line], [data-section-type="cart"] [data-line]').length;
    } catch (e) {
      return -1;
    }
  }
  var lastNoopLineCount = -1;

  function scheduleReconcile(opts) {
    opts = opts || {};
    // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): no-op cooldown.
    // Observer-triggered reconciles (from drawer repaints / upsell renders)
    // are skipped if we just did a no-op reconcile within NOOP_COOLDOWN_MS,
    // UNLESS the visible cart row count changed since that no-op (a real
    // add/remove). A real cart change also emits boost:cart-updated (picker)
    // which passes opts.force = true to bypass the cooldown outright.
    if (!opts.force && !reconcileScheduled) {
      var sinceNoop = Date.now() - lastNoopAt;
      if (sinceNoop < NOOP_COOLDOWN_MS) {
        var nowCount = domLineCount();
        // Only skip if the row count is unchanged since the no-op. If it
        // changed (or we can't count), fall through and reconcile.
        if (nowCount !== -1 && lastNoopLineCount !== -1 && nowCount === lastNoopLineCount) {
          return; // within cooldown, cart unchanged — skip this observer ping
        }
      }
    }
    if (reconcileScheduled) return;
    reconcileScheduled = true;
    setTimeout(function () {
      reconcileScheduled = false;
      reconcileNow();
    }, POST_MUTATION_DELAY);
  }

  function reconcileNow() {
    if (reconcileInFlight) {
      trailingReconcileQueued = true;
      return;
    }
    var feeVariantId = getFeeVariantId();
    if (!feeVariantId) return;
    // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): optional $50 fee.
    var embFeeVariantId = getEmbroideryFeeVariantId();

    reconcileInFlight = true;

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var result = planAndExecute(items, feeVariantId, embFeeVariantId);
        // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): if planAndExecute
        // found nothing to correct (returned null), record a no-op so the
        // cooldown can skip the storm of observer-triggered reads that
        // follow a large cart render. A real change resets this via the
        // forced reconcile path.
        if (result == null) {
          lastNoopAt = Date.now();
          lastNoopLineCount = domLineCount();
        } else {
          // A correction is being made; clear the no-op marker so follow-up
          // reconciles aren't suppressed while the cart is still settling.
          lastNoopAt = 0;
          lastNoopLineCount = -1;
        }
        return result;
      })
      .catch(function (err) {
        console.warn('[boost-cart-fee-manager] reconcile failed:', err);
      })
      .then(function () {
        reconcileInFlight = false;
        if (trailingReconcileQueued) {
          trailingReconcileQueued = false;
          setTimeout(reconcileNow, POST_MUTATION_DELAY);
        }
      });
  }

  /* ============================================
  v2.2.5 — planAndExecute
  Inspects the cart, computes all corrections, fires mutations.
  ============================================ */
  function planAndExecute(items, feeVariantId, embFeeVariantId) {
    if (!items.length) return null;

    // ---------- Pass 1: classify lines and build groups ----------
    var groupsByPk = {};
    items.forEach(function (item, idx) {
      var props = item.properties || {};
      var pk = props._product_key || '';
      if (!pk || pk.indexOf(PRODUCT_KEY_PREFIX) !== 0) return;
      if (!groupsByPk[pk]) {
        groupsByPk[pk] = {
          garmentIdx: -1,
          garmentQty: 0,
          carrierIndices: [],
          decorationIndices: [],
          isSampleOnly: false,
          feeHandles: {}   // BOOST WS2: production setup-fee handles this group requires
        };
      }
      var g = groupsByPk[pk];
      var isTrackerCarrier = (props._hidden_in_cart === 'true' || props._hidden_in_cart === true);
      var po = props._product_option || '';

      // Sample-only detection on EITHER garment (v2.2.4+) or carrier
      // (legacy v2.2 pre-tracker-removal carts).
      if (isSampleOnlyMarker(props)) {
        g.isSampleOnly = true;
      }

      if (isTrackerCarrier) {
        // Legacy hidden tracker — counts as a carrier for orphan cleanup
        // but does NOT participate in decoration-qty sync (it's zero-priced
        // and v2.2.4 doesn't write these anymore).
        g.carrierIndices.push(idx);
        return;
      }
      if (po === 'decoration') {
        // Decoration sibling (embroidery, print, transfer, etc., plus
        // team carriers like front_number/back_number/team_name/
        // player_name). Must match garment qty.
        g.carrierIndices.push(idx);
        g.decorationIndices.push(idx);
        // BOOST CUSTOMISATION 2026-07-02 (WS2): record this decoration's
        // production setup-fee handle. Prefer the explicit _setup_fee written by
        // the picker; fall back to the embroidery method for legacy carts written
        // before _setup_fee existed.
        var fh = props._setup_fee || '';
        if (!fh && props._decoration_method === 'embroidery') fh = 'embroidery-setup-fee';
        if (fh) g.feeHandles[fh] = true;
        return;
      }
      if (po === 'sample_confirmation') {
        // AS Colour acknowledgement and similar — stays at qty 1
        // regardless of garment qty. Counts as carrier for orphan
        // cleanup but NOT for decoration sync.
        g.carrierIndices.push(idx);
        return;
      }
      if (!po) {
        // Garment line (no _product_option).
        g.garmentIdx = idx;
        g.garmentQty = item.quantity;
      }
    });

    // ---------- Pass 2: compute corrections ----------
    var orphanCarrierIndices = [];
    var decorationMismatchIndices = []; // [{ idx, targetQty }, ...]
    var validDecoratedGroups = 0;
    // BOOST CUSTOMISATION 2026-07-02 (WS2): the map (handle -> variantId) and the
    // set of fee variant IDs required across all valid decorated groups.
    var feeVariantsMap = getFeeVariantsMap();
    var requiredFeeIds = {};   // variantId -> true

    Object.keys(groupsByPk).forEach(function (pk) {
      var g = groupsByPk[pk];
      if (g.garmentIdx === -1) {
        // Orphan: garment removed but carriers remain. Zero ALL
        // carriers (decoration siblings included) regardless of group
        // type. Sample-only orphans also get zeroed.
        g.carrierIndices.forEach(function (idx) { orphanCarrierIndices.push(idx); });
        return;
      }
      /* ============================================
      BOOST CUSTOMISATION 2026-09-10 (v2.5.0) — DECORATION-PRESENCE TEST.
      A group counts as decorated only when it actually HOLDS a decoration.
      Before this, the test was `!g.isSampleOnly` alone, so a garment line
      carrying a _product_key with no _product_option:'decoration' siblings
      marked the $70 Artwork Setup Fee as required forever: computeFeePlan
      read needed=true / present=true / qty=1, returned needs=null, and the
      reconcile went no-op on every pass. That is the orphaned-fee defect
      (live cart dump 2026-09-10, garment pk-0ff902 with no decorations).

      A keyed-but-bare garment is a malformed line, not a decorated one. It
      is left in place: the Ajax API cannot edit line properties without a
      remove-and-re-add, and once it stops requiring a fee it is harmless.
      Its _product_key is simply ignored from here on.
      ============================================ */
      var hasDecoration = g.decorationIndices.length > 0;
      if (!g.isSampleOnly && hasDecoration) {
        validDecoratedGroups++;
        // BOOST CUSTOMISATION 2026-07-02 (WS2): artwork fee is required whenever
        // any valid decorated group exists; each production fee is required when a
        // decoration in the group names it via _setup_fee.
        if (feeVariantId) requiredFeeIds[Number(feeVariantId)] = true;
        Object.keys(g.feeHandles).forEach(function (h) {
          var id = Number(feeVariantsMap[h]);
          // Legacy fallback: the embroidery handle may be absent from the map on
          // an older theme.liquid; use the standalone embroidery id if provided.
          if ((isNaN(id) || id <= 0) && h === 'embroidery-setup-fee' && embFeeVariantId) {
            id = Number(embFeeVariantId);
          }
          if (!isNaN(id) && id > 0) requiredFeeIds[id] = true;
        });
        // Decoration-qty sync — only for decorated groups (sample-only
        // groups have no decoration siblings to sync).
        g.decorationIndices.forEach(function (idx) {
          if (items[idx].quantity !== g.garmentQty) {
            decorationMismatchIndices.push({ idx: idx, targetQty: g.garmentQty });
          }
        });
      }
      // else: sample-only group with garment present — valid state.
    });

    // ---------- Pass 3: fee state (per fee) ----------
    // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2):
    // Two independent fees are reconciled in one cycle:
    //   - $70 Artwork Setup Fee: needed when any valid decorated group exists.
    //   - $50 Embroidery Setup Fee: needed when any valid decorated group
    //     contains an embroidery decoration. (embFeeVariantId may be null
    //     if the carrier isn't configured — then this fee is inert.)
    // Each fee resolves to one of: 'add', 'force-1', 'remove-all', or null.
    //
    // computeFeePlan locates a fee's lines, then decides the action from
    // whether the fee is needed and its current presence/quantity.
    function computeFeePlan(feeId, isNeeded) {
      var lineIndices = [];
      if (feeId) {
        items.forEach(function (item, idx) {
          if (Number(item.variant_id) === feeId) {
            lineIndices.push(idx);
          }
        });
      }
      var present = lineIndices.length > 0;
      var totalQty = 0;
      lineIndices.forEach(function (idx) { totalQty += items[idx].quantity; });

      var needs = null;
      if (isNeeded && !present) {
        needs = 'add';
      } else if (isNeeded && totalQty > 1) {
        needs = 'force-1';
      } else if (!isNeeded && present) {
        needs = 'remove-all';
      }
      return { feeId: feeId, lineIndices: lineIndices, needs: needs };
    }

    // BOOST CUSTOMISATION 2026-07-02 (WS2): build the set of all known fee
    // variant IDs to manage (the map values, plus the two legacy IDs as a
    // fallback), then compute a plan per fee: required if its ID is in
    // requiredFeeIds. The picker is the sole ADDER, so 'add' is a no-op here;
    // we only ENFORCE (dedupe to 1) and REMOVE (when no longer required).
    var knownFeeIds = [];
    var seenFeeId = {};
    function noteFeeId(id) {
      var n = Number(id);
      if (!isNaN(n) && n > 0 && !seenFeeId[n]) { seenFeeId[n] = true; knownFeeIds.push(n); }
    }
    Object.keys(feeVariantsMap).forEach(function (h) { noteFeeId(feeVariantsMap[h]); });
    noteFeeId(feeVariantId);
    if (embFeeVariantId) noteFeeId(embFeeVariantId);

    var feePlans = knownFeeIds.map(function (id) {
      return computeFeePlan(id, !!requiredFeeIds[id]);
    });

    // ---------- Pass 4: anything to do? ----------
    // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): the PICKER is now the
    // sole ADDER of fees (it injects them into its /cart/add.js call so they
    // land at the group bottom). The fee-manager no longer adds — it only
    // ENFORCES (dedupe to qty 1) and REMOVES (when no decorated/embroidery
    // groups remain). So a 'add' plan here is a NO-OP: we deliberately ignore
    // it. This guarantees a single adder (picker) and a single remover
    // (manager), with no add/observe/add race. If the picker ever fails to
    // add a needed fee (e.g. its pre-add cart read failed), the fee is simply
    // absent — acceptable and self-correcting on the next decorated add — and
    // never duplicated.
    var anyFeeRemoveOrForce = feePlans.some(function (p) {
      return p.needs === 'remove-all' || p.needs === 'force-1';
    });

    var hasPositionalWork = orphanCarrierIndices.length > 0
                         || decorationMismatchIndices.length > 0
                         || anyFeeRemoveOrForce;

    if (!hasPositionalWork) {
      return null; // Nothing to do; bail. (Adds are the picker's job now.)
    }

    // ---------- Pass 5: build combined updates payload ----------
    /* ============================================
    BOOST CUSTOMISATION 2026-09-10 (v2.5.0) — KEY-ADDRESSED WRITES.
    The payload is an `updates` OBJECT keyed by each line's immutable
    Shopify `key`, never a positional array. Shopify applies each entry to
    exactly the line carrying that key and leaves every other line alone,
    so a cart that changes between our /cart.js read and this POST can no
    longer shift our zeros onto the wrong lines. That shift is how a
    surviving garment lost its decoration carriers while keeping its
    _product_key (the corruption behind the orphaned-fee defect), and it is
    the same failure boost-bulk-grid.js fixed the same way on 2026-05-23.

    Keys also disambiguate lines that share a variant id, which positions
    and variant-keyed updates both get wrong.
    ============================================ */
    var promise = Promise.resolve(null);

    if (hasPositionalWork) {
      var keyUpdates = {};

      // Assigned to a var, not a block-scoped function declaration, so the
      // file stays ES5-safe in strict mode.
      var setQtyByIndex = function (idx, qty) {
        var line = items[idx];
        if (!line || !line.key) return;   // No key: skip rather than guess a position.
        keyUpdates[line.key] = qty;
      };

      // Decoration qty sync must be written BEFORE orphan zeroing so a
      // mismatched decoration that's about to be zeroed (because its garment
      // was removed) doesn't keep a non-zero target. Later assignment to the
      // same key wins, so this ordering is preserved exactly as it was under
      // the positional array.
      decorationMismatchIndices.forEach(function (m) {
        setQtyByIndex(m.idx, m.targetQty);
      });
      orphanCarrierIndices.forEach(function (idx) { setQtyByIndex(idx, 0); });

      // Apply remove-all / force-1 for every known fee in the same payload.
      // (No 'add' handling here — see Pass 4 note: the picker adds fees.)
      feePlans.forEach(function (plan) {
        if (plan.needs === 'remove-all') {
          plan.lineIndices.forEach(function (idx) { setQtyByIndex(idx, 0); });
        } else if (plan.needs === 'force-1') {
          plan.lineIndices.forEach(function (idx, k) {
            setQtyByIndex(idx, (k === 0) ? 1 : 0);
          });
        }
      });

      // Every correction we planned resolved to a line with no key. Nothing
      // safe to write, so bail rather than fall back to a positional array.
      if (!Object.keys(keyUpdates).length) return null;

      promise = promise.then(function () { return fireKeyedUpdate(keyUpdates); });
    }

    return promise;
  }

  /* ============================================
  BOOST CUSTOMISATION 2026-09-10 (v2.5.0): renamed from
  firePositionalUpdate. Sends the key-addressed `updates` object, checks the
  response status (the old version piped a 422 body straight into
  refreshCartUi, which returned early on the missing `sections` and left the
  failure invisible), and retries ONCE on failure so a write lost to a
  concurrent cart change is not simply abandoned.
  ============================================ */
  var updateRetries = 0;
  var MAX_UPDATE_RETRIES = 1;

  function fireKeyedUpdate(keyUpdates) {
    suppressMutationObserver = true;
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        updates: keyUpdates,
        sections: getSectionsList()
      })
    })
      .then(function (r) {
        if (!r.ok) {
          return r.text().then(function (t) {
            throw new Error('cart/update.js (key) failed: ' + r.status + ' ' + t);
          });
        }
        return r.json();
      })
      .then(function (response) {
        updateRetries = 0;
        refreshCartUi(response);
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            suppressMutationObserver = false;
          });
        });
      })
      .catch(function (err) {
        suppressMutationObserver = false;
        console.warn('[boost-cart-fee-manager] keyed update failed:', err);
        // One forced retry against a freshly-read cart. Forced so the no-op
        // cooldown cannot swallow it; bounded so a persistent failure (e.g. a
        // sold-out line) can never loop.
        if (updateRetries < MAX_UPDATE_RETRIES) {
          updateRetries++;
          setTimeout(function () { scheduleReconcile({ force: true }); }, 300);
        } else {
          updateRetries = 0;
        }
      });
  }

  // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): addFee is RETAINED but
  // no longer called. Fee ADDING moved to the picker (so fees ride the
  // garment's /cart/add.js call and land at the group bottom). This function
  // is kept as a documented fallback in case the ownership split is reverted;
  // it is intentionally unreferenced. Do not wire it back into planAndExecute
  // without also removing the picker's fee-injection, or fees will duplicate.
  function addFee(feeVariantId, feeType) {
    // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): feeType marks the
    // line ('artwork_setup' for $70, 'embroidery_setup' for $50).
    var ft = feeType || 'artwork_setup';
    suppressMutationObserver = true;
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: feeVariantId,
          quantity: 1,
          properties: { _fee_type: ft }
        }],
        sections: getSectionsList()
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        refreshCartUi(response);
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            suppressMutationObserver = false;
          });
        });
      })
      .catch(function (err) {
        suppressMutationObserver = false;
        console.warn('[boost-cart-fee-manager] add fee failed:', err);
      });
  }

  /* ============================================
  v2.2.5 TRIGGERS — MutationObserver + DOMContentLoaded
  ============================================ */

  var drawerObserver = null;
  var cartPageObserver = null;
  // Track current observed nodes so we can re-attach if the theme
  // replaces them (e.g. on cart page re-init).
  var observedDrawer = null;
  var observedCartPageWrapper = null;

  function handleMutation() {
    if (suppressMutationObserver) return;
    scheduleReconcile();
  }

  function attachDrawerObserver() {
    var drawer = document.getElementById('cart');
    if (!drawer) return;
    if (observedDrawer === drawer) return; // Already attached to this node.
    if (drawerObserver) drawerObserver.disconnect();
    drawerObserver = new MutationObserver(handleMutation);
    drawerObserver.observe(drawer, { childList: true, subtree: true });
    observedDrawer = drawer;
  }

  function attachCartPageObserver() {
    var cartPageId = getCartPageSectionId();
    if (!cartPageId) {
      // Not on the cart page; nothing to observe.
      if (cartPageObserver) {
        cartPageObserver.disconnect();
        cartPageObserver = null;
        observedCartPageWrapper = null;
      }
      return;
    }
    var wrapper = document.getElementById('shopify-section-' + cartPageId);
    if (!wrapper) return;
    if (observedCartPageWrapper === wrapper) return;
    if (cartPageObserver) cartPageObserver.disconnect();
    cartPageObserver = new MutationObserver(handleMutation);
    cartPageObserver.observe(wrapper, { childList: true, subtree: true });
    observedCartPageWrapper = wrapper;
  }

  function attachObservers() {
    attachDrawerObserver();
    attachCartPageObserver();
  }

  /* ============================================
  v2.2.5 — re-attach observers on ajaxCart event
  The theme fires the global 'ajaxCart' event after it (or we) replace
  cart-page innerHTML. The wrapper element identity is preserved by
  innerHTML replacement, but the drawer container can sometimes be
  re-parented; in either case re-attaching is cheap and idempotent.
  ============================================ */
  function onAjaxCart() {
    // Brief delay to let the theme's ajaxCart.init() finish setting up.
    setTimeout(attachObservers, 50);
  }

  function init() {
    var feeVariantId = getFeeVariantId();
    if (!feeVariantId) {
      console.warn('[boost-cart-fee-manager] window.BoostFeeConfig.feeVariantId not set. Fee management disabled.');
      return;
    }

    attachObservers();

    // Re-attach when the theme repaints/rebinds.
    window.addEventListener('ajaxCart', onAjaxCart);

    // Picker dispatches this after its own /cart/add.js calls; allows
    // instant reconcile without waiting for DOM-observer debounce. This is
    // a REAL cart change, so force past the no-op cooldown.
    document.addEventListener('boost:cart-updated', function (e) {
      // BOOST CUSTOMISATION 2026-06-04 (v2.4.1): the artwork picker is the sole
      // fee adder and runs a pre-add /cart.js read, so it never adds a duplicate
      // fee. After a picker add the cart's fees are already correct, which means
      // the forced reconcile here was a redundant /cart.js round trip that (with
      // the drawer repaint) produced a visible delay before the drawer opened.
      // For picker-sourced events we now SKIP the reconcile and instead set the
      // no-op cooldown marker with the post-add row count, so the drawer-repaint
      // MutationObserver pings are also suppressed. Every other source (manual
      // qty edits, line removals, non-picker adds) still forces a full reconcile.
      var src = e && e.detail && e.detail.source;
      if (src === 'artwork-picker') {
        lastNoopAt = Date.now();
        lastNoopLineCount = domLineCount();
        return;
      }
      scheduleReconcile({ force: true });
    });

    // Initial reconcile after a short delay. Covers the case where a
    // previous session left the cart in a mismatched state.
    setTimeout(function () { reconcileNow(); }, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================
BOOST CUSTOMISATION END
============================================ */
